import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { Navigate, useNavigate } from 'react-router-dom';

import { Typography } from '@speeki/global-ui-components';
import { useQueryInfinitePaginationV2 } from '@speeki/react-hooks';

import { ArrElement } from '@@types/CommonTypes';

import { ContainerSection } from '@components/_global/GlobalStyle/Global.styled';
import { Container } from '@components/ions/layouts';
import { DefaultLayout } from '@components/templates/DefaultLayout/DefaultLayout';

import {
    CaseReportsInput,
    ReportCasesDocument,
    ReportCasesQuery,
    ReportCasesQueryVariables,
} from '@graphql/generated/graphql';

import { getPath } from '@services/RouteService/paths';

import { useLazyQuery, useReactiveVar } from '@apollo/client';
import {
    authVar,
    resetReporterDetails,
    translationsStatusVar,
} from '@boot/reactiveVariables';
import { CasesSearchForm } from '@domains/cases/molecules/CasesSearchForm/CasesSearchForm';
import { CasesList } from '@domains/cases/organisms/CasesList/CasesList';

import {
    SearchWrapper,
    StyledButtonIconText,
    StyledContainerInner,
    StyledContainerSection,
} from './CasesListPage.styled';

const LIST_SIZE = 10;
const INITIAL_SORT: CaseReportsInput['sorting'] = [
    {
        direction: 'DESC',
        field: 'CREATED_AT',
    },
];

export const CasesListPage = () => {
    const [searchPhrase, setSearchPhrase] = useState('');

    const navigate = useNavigate();
    const { t } = useTranslation();
    const isGuest = useReactiveVar(authVar).isGuest;
    const loadingTranslations =
        useReactiveVar(translationsStatusVar) === 'loading';

    const [searchReportCases, { called, data, error, fetchMore, loading }] =
        useLazyQuery(ReportCasesDocument, {
            context: {
                notifyOnError: true,
            },
            fetchPolicy: 'cache-and-network',
            notifyOnNetworkStatusChange: true,
            variables: {
                input: {
                    filter: searchPhrase,
                    paging: {
                        first: LIST_SIZE,
                    },
                    sorting: INITIAL_SORT,
                },
            },
        });

    const list = data?.list;

    const queryResult = useQueryInfinitePaginationV2<
        ArrElement<ReportCasesQuery['list']['edges']>['node'],
        ReportCasesQueryVariables
    >({
        edges: list?.edges || [],
        fetchMore: fetchMore,
        loading,
        pageInfo: list?.pageInfo,
        variables: {
            input: {
                filter: searchPhrase,
                paging: {
                    first: LIST_SIZE,
                    last: LIST_SIZE,
                },
                sorting: INITIAL_SORT,
            },
        },
    });

    const { isEmptyList } = queryResult;
    const displaySearchForm = searchPhrase || !isEmptyList;

    useEffect(() => {
        void searchReportCases();
    }, [searchPhrase]);

    if (isGuest) return <Navigate to={getPath({ path: '/incidents' })} />;

    return (
        <DefaultLayout>
            <Container>
                <StyledContainerInner>
                    <ContainerSection>
                        {loadingTranslations && (
                            <Typography.TextH3_24_34>
                                <Skeleton height={25} width={150} />
                            </Typography.TextH3_24_34>
                        )}
                        {!loadingTranslations && (
                            <Typography.TextH3_24_34>
                                {t('MY_CASES_TEXT')}
                            </Typography.TextH3_24_34>
                        )}
                    </ContainerSection>
                    <StyledContainerSection>
                        <SearchWrapper>
                            {displaySearchForm && (
                                <CasesSearchForm
                                    setSearchPhrase={setSearchPhrase}
                                />
                            )}
                            <StyledButtonIconText
                                iconProps={{
                                    icon: 'SvgAddNew',
                                    size: 24,
                                }}
                                onPress={() => {
                                    navigate(
                                        getPath({ path: '/new-conversation' }),
                                    );
                                    resetReporterDetails();
                                }}
                                testId="M1iTsnJ"
                            />
                        </SearchWrapper>
                    </StyledContainerSection>
                </StyledContainerInner>
                <CasesList
                    queryResult={{
                        ...queryResult,
                        called,
                        error: !!error,
                        loading,
                    }}
                    searchPhrase={searchPhrase}
                />
            </Container>
        </DefaultLayout>
    );
};
